import { Component, ChangeDetectionStrategy, inject } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { AccountObject } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { SelectSearchModule } from 'src/components/select-search/select-search.module'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { BuyersGroup, BuyersGroupApiService } from 'src/pages/admin/settings/buyers-groups/buyers-groups-item/buyers-groups.service'
import { NonBuyersWarningComponent } from '../../non-buyers-warning/non-buyers-warning.component'

export interface AddToBuyerGroupsDialogData {
  selectedCompanies: DeepReadonly<AccountObject>[]
}

@Component({
  selector: 'tc-add-to-buyer-groups-dialog',
  templateUrl: './add-to-buyer-groups-dialog.component.html',
  styleUrls: ['./add-to-buyer-groups-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    SelectSearchModule,
    NonBuyersWarningComponent,
  ],
})
export class AddToBuyerGroupsDialogComponent {
  private dialogRef = inject<MatDialogRef<AddToBuyerGroupsDialogComponent>>(MatDialogRef)
  data = inject<AddToBuyerGroupsDialogData>(MAT_DIALOG_DATA)
  private buyersGroupService = inject(BuyersGroupApiService)
  private toaster = inject(ToasterService)

  buyerGroups: BuyersGroup[] = []
  groupCtrl = new FormControl<string[]>(null)
  loading = true
  saving = false
  buyers: DeepReadonly<AccountObject>[] = []

  constructor() {
    this.loadGroups()
  }

  private async loadGroups() {
    try {
      const groups = await this.buyersGroupService.list()
      this.buyerGroups = groups
      this.loading = false
    } catch {
      this.toaster.error('Unable to load buyer groups.')
      this.loading = false
    }
  }

  cancel() {
    this.dialogRef.close()
  }

  async add() {
    const groupIds = this.groupCtrl.value
    if (!groupIds || groupIds.length === 0) {
      this.toaster.error('Please select at least one buyer group.')
      return
    }

    this.saving = true
    try {
      const selectedBuyerIds = this.buyers.map(c => String(c.account))
      for (const groupId of groupIds) {
        const group = this.buyerGroups.find(g => g.group_id === groupId)
        if (!group) continue
        const mergedBuyers = [...new Set([...group.buyers, ...selectedBuyerIds])]
        const updatedGroup: BuyersGroup = { ...group, buyers: mergedBuyers }
        await this.buyersGroupService.update(updatedGroup)
      }
      const groupNames = groupIds.map(id => this.buyerGroups.find(g => g.group_id === id)?.name).filter(Boolean).join(', ')
      this.toaster.success(`Successfully added ${selectedBuyerIds.length} ${selectedBuyerIds.length === 1 ? 'buyer' : 'buyers'} to: ${groupNames}.`)
      this.dialogRef.close(true)
    } catch {
      this.toaster.error('Unable to add companies to buyer groups.')
    } finally {
      this.saving = false
    }
  }
}
